// Homesteads: the shared geometry of a claim, its structures, and the
// furniture inside them. Engine-agnostic on purpose — the server validates
// against exactly what the client renders and predicts with, which is the
// world-plan philosophy ("what you see is what you collide with") extended
// to buildings a player put there.
//
// What lives here is SHAPE: footprints, grids, room plans, id formats, and
// the terrain-fit arithmetic both sides must agree on. What does NOT live
// here is tuning that only the server needs — costs, spacing rules, refusal
// thresholds — which stays in apps/server/src/housing/catalog.ts where
// retuning is a deploy rather than a protocol change.

import type { Aabb } from "./constants";
import { heightAt } from "./terrain";
import type { AreaPlan, PortalDef } from "./world-plan";
import { isAreaId, planFor, SURFACE_ID } from "./world-plan";

// ---------------------------------------------------------------------------
// Structures.

export const STRUCTURE_KINDS = ["cabin", "shed"] as const;
export type StructureKind = (typeof STRUCTURE_KINDS)[number];

export function isStructureKind(value: string): value is StructureKind {
  return (STRUCTURE_KINDS as readonly string[]).includes(value);
}

/**
 * A structure's exterior shell on the surface, in metres. Width runs along
 * the door face; depth runs front-to-back. The interior room is smaller
 * than the shell (walls have thickness in the fiction, and the interior
 * grid wants whole cells).
 */
export interface StructureShape {
  /** Exterior shell footprint. */
  width: number;
  depth: number;
  /** Exterior wall height, for the renderer. */
  height: number;
  /** Interior room half-extents (the AreaPlan the portal leads to). */
  interiorHalfW: number;
  interiorHalfD: number;
  interiorWallHeight: number;
  /** The public words for this kind. */
  label: string;
}

export const STRUCTURE_SHAPES: Record<StructureKind, StructureShape> = {
  cabin: {
    width: 10,
    depth: 8,
    height: 4.2,
    interiorHalfW: 4.5,
    interiorHalfD: 3.5,
    interiorWallHeight: 3.2,
    label: "cabin",
  },
  shed: {
    width: 8,
    depth: 6.5,
    height: 3.6,
    interiorHalfW: 3.5,
    interiorHalfD: 3,
    interiorWallHeight: 3,
    label: "workshop shed",
  },
};

/**
 * Structures face one of four ways — a quarter turn each — so every
 * footprint, door zone and furniture box stays an axis-aligned Aabb, which
 * is the only collision shape the movement integrator knows. Quarter 0
 * fronts south (+z, toward the settlement-street convention of doors on
 * the south face); 1 fronts east; 2 north; 3 west.
 */
export type Quarter = 0 | 1 | 2 | 3;

export function asQuarter(value: number): Quarter {
  const q = ((Math.round(value) % 4) + 4) % 4;
  return q as Quarter;
}

/** The quarter whose facing best matches a yaw (radians, atan2 convention). */
export function quarterFromYaw(yaw: number): Quarter {
  // World yaw: 0 faces +z? Movement treats yaw as a facing angle used by
  // the renderer; here we only need a stable quantization, shared by both
  // sides. Map yaw to the nearest cardinal.
  const tau = Math.PI * 2;
  const norm = ((yaw % tau) + tau) % tau;
  return (Math.round(norm / (Math.PI / 2)) % 4) as Quarter;
}

/** Exterior half-extents after rotation: quarters 1 and 3 swap width/depth. */
export function structureHalfExtents(
  kind: StructureKind,
  quarter: Quarter,
): { halfX: number; halfZ: number } {
  const shape = STRUCTURE_SHAPES[kind];
  const swap = quarter === 1 || quarter === 3;
  return {
    halfX: (swap ? shape.depth : shape.width) / 2,
    halfZ: (swap ? shape.width : shape.depth) / 2,
  };
}

/** The exterior shell as a world Aabb (movement collides with this). */
export function structureFootprint(
  kind: StructureKind,
  x: number,
  z: number,
  quarter: Quarter,
): Aabb {
  const { halfX, halfZ } = structureHalfExtents(kind, quarter);
  return { minX: x - halfX, maxX: x + halfX, minZ: z - halfZ, maxZ: z + halfZ };
}

/** Unit vector the door faces, per quarter (0:+z, 1:+x, 2:-z, 3:-x). */
export function quarterFacing(quarter: Quarter): { x: number; z: number } {
  switch (quarter) {
    case 0:
      return { x: 0, z: 1 };
    case 1:
      return { x: 1, z: 0 };
    case 2:
      return { x: 0, z: -1 };
    default:
      return { x: -1, z: 0 };
  }
}

/** Centre of the door on the front face, in world coordinates. */
export function structureDoorPos(
  kind: StructureKind,
  x: number,
  z: number,
  quarter: Quarter,
): { x: number; z: number } {
  const { halfX, halfZ } = structureHalfExtents(kind, quarter);
  const facing = quarterFacing(quarter);
  return { x: x + facing.x * halfX, z: z + facing.z * halfZ };
}

/**
 * The doorway trigger on the surface: a thin box just outside the door.
 * Walking into it asks the door to open — exactly the street-door idiom.
 */
export function structureDoorZone(
  kind: StructureKind,
  x: number,
  z: number,
  quarter: Quarter,
): Aabb {
  const door = structureDoorPos(kind, x, z, quarter);
  const facing = quarterFacing(quarter);
  const along = 1.3; // half-width of the doorway along the wall
  const out = 0.9; // how far the trigger reaches out from the wall
  if (facing.z !== 0) {
    return {
      minX: door.x - along,
      maxX: door.x + along,
      minZ: facing.z > 0 ? door.z : door.z - out,
      maxZ: facing.z > 0 ? door.z + out : door.z,
    };
  }
  return {
    minX: facing.x > 0 ? door.x : door.x - out,
    maxX: facing.x > 0 ? door.x + out : door.x,
    minZ: door.z - along,
    maxZ: door.z + along,
  };
}

/** Where you stand after stepping OUT of the structure onto the surface. */
export function structureExitArrival(
  kind: StructureKind,
  x: number,
  z: number,
  quarter: Quarter,
): { x: number; z: number; yaw: number } {
  const door = structureDoorPos(kind, x, z, quarter);
  const facing = quarterFacing(quarter);
  // 2.2 m out from the wall, facing away from the building — the same
  // margin the street arrivals use so the doorway trigger cannot re-grab.
  return {
    x: door.x + facing.x * 2.2,
    z: door.z + facing.z * 2.2,
    yaw: Math.atan2(facing.x, facing.z),
  };
}

// ---------------------------------------------------------------------------
// Terrain fit. Both sides evaluate the same deterministic heightfield, so
// the foundation the client renders is exactly the one the server ruled on.

/** Sample points across a footprint: corners, edge midpoints, centre. */
function footprintSamples(box: Aabb): Array<{ x: number; z: number }> {
  const midX = (box.minX + box.maxX) / 2;
  const midZ = (box.minZ + box.maxZ) / 2;
  return [
    { x: box.minX, z: box.minZ },
    { x: box.maxX, z: box.minZ },
    { x: box.minX, z: box.maxZ },
    { x: box.maxX, z: box.maxZ },
    { x: midX, z: box.minZ },
    { x: midX, z: box.maxZ },
    { x: box.minX, z: midZ },
    { x: box.maxX, z: midZ },
    { x: midX, z: midZ },
  ];
}

export interface FoundationFit {
  /** Highest and lowest ground under the footprint. */
  top: number;
  bottom: number;
  /** Worst rise over the footprint's short run — the legality number. */
  grade: number;
  /** Floor elevation: the plate sits on the highest ground plus a sill. */
  floorY: number;
}

/** How high the floor plate sits above the highest ground sample. */
export const FOUNDATION_SILL_M = 0.18;

/**
 * Measure how a structure footprint meets the ground. Pure — callers
 * decide what grade is legal; the renderer uses floorY/bottom to build a
 * plinth that reaches the lowest corner, so nothing floats.
 */
export function foundationFit(kind: StructureKind, x: number, z: number, quarter: Quarter) {
  const box = structureFootprint(kind, x, z, quarter);
  const heights = footprintSamples(box).map((p) => heightAt(p.x, p.z));
  let top = heights[0] as number;
  let bottom = heights[0] as number;
  for (const h of heights) {
    if (h > top) top = h;
    if (h < bottom) bottom = h;
  }
  const run = Math.min(box.maxX - box.minX, box.maxZ - box.minZ);
  const fit: FoundationFit = {
    top,
    bottom,
    grade: (top - bottom) / run,
    floorY: top + FOUNDATION_SILL_M,
  };
  return fit;
}

// ---------------------------------------------------------------------------
// Home room ids. A home interior is its own room; the id carries the kind
// so both sides can synthesize the same plan without a database.

export const HOME_ID_PREFIX = "home:";

export function makeHomeRoomId(kind: StructureKind, structureId: string): string {
  return `${HOME_ID_PREFIX}${kind}:${structureId}`;
}

export function parseHomeRoomId(
  roomId: string,
): { kind: StructureKind; structureId: string } | undefined {
  if (!roomId.startsWith(HOME_ID_PREFIX)) return undefined;
  const rest = roomId.slice(HOME_ID_PREFIX.length);
  const sep = rest.indexOf(":");
  if (sep <= 0) return undefined;
  const kind = rest.slice(0, sep);
  const structureId = rest.slice(sep + 1);
  if (!isStructureKind(kind) || structureId.length === 0) return undefined;
  return { kind, structureId };
}

export function isHomeRoomId(roomId: string): boolean {
  return parseHomeRoomId(roomId) !== undefined;
}

/**
 * The interior floor plan of a home. Synthesized from the room id alone so
 * client and server agree without asking anyone; the server replaces the
 * exit arrival with the structure's real doorstep once it has the row
 * (the client never reads portal arrivals — they are server business).
 */
export function homePlan(
  roomId: string,
  exitArrival: { x: number; z: number; yaw: number } = { x: 0, z: 0, yaw: 0 },
): AreaPlan {
  const parsed = parseHomeRoomId(roomId);
  const kind: StructureKind = parsed?.kind ?? "cabin";
  const shape = STRUCTURE_SHAPES[kind];
  const halfW = shape.interiorHalfW;
  const halfD = shape.interiorHalfD;
  const exit: PortalDef = {
    id: "exit",
    zone: { minX: -1.3, maxX: 1.3, minZ: halfD - 0.6, maxZ: halfD },
    target: SURFACE_ID,
    arrival: exitArrival,
    label: "outside",
  };
  return {
    id: roomId,
    label: shape.label,
    kind: "home",
    halfWidth: halfW,
    halfDepth: halfD,
    wallHeight: shape.interiorWallHeight,
    obstacles: [],
    portals: [exit],
    // Just inside the door, facing into the room, with a radius that can
    // never scatter a joiner into the exit zone (which would bounce them
    // straight back outside). The camera reason the workshop spawn is
    // well inside applies here too.
    spawn: { x: 0, z: halfD - 2.0, radius: 0.6, yaw: Math.PI },
    accent: 0xd8a05a,
  };
}

/**
 * Every room id the world can hand a client, resolved to a plan: the
 * static PLANS, or a home plan synthesized from its id. The one place a
 * "which plan?" question should be asked once homes exist.
 */
export function resolvePlan(roomId: string): AreaPlan {
  if (isAreaId(roomId)) return planFor(roomId);
  if (isHomeRoomId(roomId)) return homePlan(roomId);
  throw new Error(`unknown area: ${roomId}`);
}

/** Is this id any room a character may legitimately be in? */
export function isKnownRoomId(roomId: string): boolean {
  return isAreaId(roomId) || isHomeRoomId(roomId);
}

// ---------------------------------------------------------------------------
// The interior grid. Furniture lands on whole cells so "where" is a place
// you stood, never a coordinate you typed.

export const HOME_GRID_CELL_M = 1;

export interface HomeGrid {
  cols: number;
  rows: number;
  minX: number;
  minZ: number;
}

export function homeGrid(kind: StructureKind): HomeGrid {
  const shape = STRUCTURE_SHAPES[kind];
  const cols = Math.floor((shape.interiorHalfW * 2) / HOME_GRID_CELL_M);
  const rows = Math.floor((shape.interiorHalfD * 2) / HOME_GRID_CELL_M);
  return {
    cols,
    rows,
    minX: -(cols * HOME_GRID_CELL_M) / 2,
    minZ: -(rows * HOME_GRID_CELL_M) / 2,
  };
}

/** Snap a standing position to the nearest grid cell (may be out of range). */
export function cellAt(grid: HomeGrid, x: number, z: number): { cellX: number; cellZ: number } {
  return {
    cellX: Math.floor((x - grid.minX) / HOME_GRID_CELL_M),
    cellZ: Math.floor((z - grid.minZ) / HOME_GRID_CELL_M),
  };
}

/** World-space centre of a cell. */
export function cellCenter(grid: HomeGrid, cellX: number, cellZ: number): { x: number; z: number } {
  return {
    x: grid.minX + (cellX + 0.5) * HOME_GRID_CELL_M,
    z: grid.minZ + (cellZ + 0.5) * HOME_GRID_CELL_M,
  };
}

// ---------------------------------------------------------------------------
// Placeables. The clean seam crafted furniture plugs into later: a
// placeable is (id, kind, footprint, render kind, provenance pointer) and
// nothing here cares whether the world provided it or a crafter did.

export const PLACEABLE_KINDS = [
  "cot",
  "table",
  "lamp",
  "chest",
  "rug",
  "workbench",
  "bench",
] as const;
export type PlaceableKind = (typeof PLACEABLE_KINDS)[number];

export function isPlaceableKind(value: string): value is PlaceableKind {
  return (PLACEABLE_KINDS as readonly string[]).includes(value);
}

/**
 * Kinds that only ever arrive as crafted artifacts. A crafted piece was
 * paid for at the bench, so it never appears in the `/furnish` unit-cost
 * table; its row carries a provenance pointer, and its placement is the
 * artifact's placement (the two rows move in one transaction).
 */
export const CRAFTED_PLACEABLE_KINDS = ["bench"] as const;
export type CraftedPlaceableKind = (typeof CRAFTED_PLACEABLE_KINDS)[number];
/** The world-provided kinds `/furnish` knocks together for a bulk cost. */
export type NativePlaceableKind = Exclude<PlaceableKind, CraftedPlaceableKind>;

export function isCraftedPlaceableKind(value: PlaceableKind): value is CraftedPlaceableKind {
  return (CRAFTED_PLACEABLE_KINDS as readonly string[]).includes(value);
}

export interface PlaceableSpec {
  kind: PlaceableKind;
  /** Footprint in grid cells at rotation 0: w along x, d along z. */
  w: number;
  d: number;
  /** Solid furniture collides; a rug does not. */
  solid: boolean;
  /** The words a prompt uses. */
  label: string;
}

export const PLACEABLE_SPECS: Record<PlaceableKind, PlaceableSpec> = {
  cot: { kind: "cot", w: 1, d: 2, solid: true, label: "cot" },
  table: { kind: "table", w: 2, d: 2, solid: true, label: "table" },
  lamp: { kind: "lamp", w: 1, d: 1, solid: true, label: "lamp" },
  chest: { kind: "chest", w: 2, d: 1, solid: true, label: "chest" },
  rug: { kind: "rug", w: 2, d: 3, solid: false, label: "rug" },
  workbench: { kind: "workbench", w: 3, d: 1, solid: true, label: "workbench" },
  // Crafted at the joiner's bench (a 1.8 m × 0.6 m footprint on the
  // one-metre grid), never knocked together at the door.
  bench: { kind: "bench", w: 2, d: 1, solid: true, label: "settle bench" },
};

/** Footprint in cells after rotation (quarters 1/3 swap w and d). */
export function placeableCells(kind: PlaceableKind, rot: Quarter): { w: number; d: number } {
  const spec = PLACEABLE_SPECS[kind];
  const swap = rot === 1 || rot === 3;
  return { w: swap ? spec.d : spec.w, d: swap ? spec.w : spec.d };
}

/** Does a placeable at (cellX, cellZ, rot) fit inside the grid? */
export function cellsInBounds(
  grid: HomeGrid,
  kind: PlaceableKind,
  cellX: number,
  cellZ: number,
  rot: Quarter,
): boolean {
  const { w, d } = placeableCells(kind, rot);
  return cellX >= 0 && cellZ >= 0 && cellX + w <= grid.cols && cellZ + d <= grid.rows;
}

/** The world-space box a placeable occupies. */
export function placeableAabb(
  grid: HomeGrid,
  kind: PlaceableKind,
  cellX: number,
  cellZ: number,
  rot: Quarter,
): Aabb {
  const { w, d } = placeableCells(kind, rot);
  return {
    minX: grid.minX + cellX * HOME_GRID_CELL_M,
    maxX: grid.minX + (cellX + w) * HOME_GRID_CELL_M,
    minZ: grid.minZ + cellZ * HOME_GRID_CELL_M,
    maxZ: grid.minZ + (cellZ + d) * HOME_GRID_CELL_M,
  };
}

function boxesOverlap(a: Aabb, b: Aabb): boolean {
  return a.minX < b.maxX && a.maxX > b.minX && a.minZ < b.maxZ && a.maxZ > b.minZ;
}

/**
 * The strip inside the exit door that must stay clear, so furniture can
 * never wall a visitor in (or block the arrival pose the portal persists).
 */
export function doorClearance(kind: StructureKind): Aabb {
  const shape = STRUCTURE_SHAPES[kind];
  return {
    minX: -1.6,
    maxX: 1.6,
    minZ: shape.interiorHalfD - 2.4,
    maxZ: shape.interiorHalfD,
  };
}

export interface PlacedLike {
  kind: string;
  cellX: number;
  cellZ: number;
  rot: number;
  /** A piece in the owner's arms occupies no floor. */
  carried?: boolean;
}

/**
 * Where a piece lands when set down where a body stands: the footprint
 * centred on the boots, snapped to whole cells. Shared so the client's
 * ghost preview and the server's ruling anchor identically.
 */
export function anchorForPlacement(
  grid: HomeGrid,
  kind: PlaceableKind,
  rot: Quarter,
  x: number,
  z: number,
): { cellX: number; cellZ: number } {
  const { w, d } = placeableCells(kind, rot);
  return {
    cellX: Math.round((x - grid.minX) / HOME_GRID_CELL_M - w / 2),
    cellZ: Math.round((z - grid.minZ) / HOME_GRID_CELL_M - d / 2),
  };
}

export type PlacementRefusal = "unknown-kind" | "out-of-bounds" | "doorway" | "occupied";

/**
 * May a placeable land here? Shared so the client's ghost preview and the
 * server's ruling are the same arithmetic. `others` is everything already
 * placed (the one being moved excluded by the caller).
 */
export function placementRefusal(
  structureKind: StructureKind,
  kind: string,
  cellX: number,
  cellZ: number,
  rot: Quarter,
  others: readonly PlacedLike[],
): PlacementRefusal | undefined {
  if (!isPlaceableKind(kind)) return "unknown-kind";
  const grid = homeGrid(structureKind);
  if (!Number.isInteger(cellX) || !Number.isInteger(cellZ)) return "out-of-bounds";
  if (!cellsInBounds(grid, kind, cellX, cellZ, rot)) return "out-of-bounds";
  const box = placeableAabb(grid, kind, cellX, cellZ, rot);
  if (boxesOverlap(box, doorClearance(structureKind))) return "doorway";
  for (const other of others) {
    if (other.carried === true) continue;
    if (!isPlaceableKind(other.kind)) continue;
    const otherBox = placeableAabb(
      grid,
      other.kind,
      other.cellX,
      other.cellZ,
      asQuarter(other.rot),
    );
    if (boxesOverlap(box, otherBox)) return "occupied";
  }
  return undefined;
}

/**
 * Solid furniture as movement obstacles. Both integrators call this on the
 * same replicated rows, so a table blocks you identically on both sides of
 * the wire and prediction never fights the server.
 */
export function placeableObstacles(
  structureKind: StructureKind,
  placed: readonly PlacedLike[],
): Aabb[] {
  const grid = homeGrid(structureKind);
  const boxes: Aabb[] = [];
  for (const p of placed) {
    if (p.carried === true) continue;
    if (!isPlaceableKind(p.kind)) continue;
    if (!PLACEABLE_SPECS[p.kind].solid) continue;
    boxes.push(placeableAabb(grid, p.kind, p.cellX, p.cellZ, asQuarter(p.rot)));
  }
  return boxes;
}

// ---------------------------------------------------------------------------
// Claim names. Player-authored, but they appear on a surface every passerby
// reads, so the shape is constrained: letters, spaces, and a few marks.
// Digits are refused — a stake carries a name, not a number.

export const CLAIM_NAME_MIN = 3;
export const CLAIM_NAME_MAX = 32;

export function sanitizeClaimName(raw: unknown): string | undefined {
  if (typeof raw !== "string") return undefined;
  const name = raw.replace(/\s+/g, " ").trim();
  if (name.length < CLAIM_NAME_MIN || name.length > CLAIM_NAME_MAX) return undefined;
  if (!/^[\p{L}][\p{L} '&-]*$/u.test(name)) return undefined;
  return name;
}

/** How close you must stand to a stake or a door to read/work it. */
export const CLAIM_READ_RANGE_M = 6;
